export const CORN_HALF_DOZEN_QUANTITY = 6;
export const CORN_DOZEN_QUANTITY = 12;
export const CORN_HALF_DOZEN_PRICE = 4.00;
export const CORN_DOZEN_PRICE = 8.00;
export const EGG_BULK_THRESHOLD = 5;
export const EGG_BULK_UNIT_PRICE = 8.00;

export const normalizeQuantity = (value, fallback = 1) => {
  const quantity = parseInt(value, 10);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : fallback;
};

export const toPriceNumber = (value, fallback = 0) => {
  const price = parseFloat(value);
  return Number.isFinite(price) ? price : fallback;
};

const getProductName = (productOrName) => {
  if (typeof productOrName === 'string') {
    return productOrName;
  }

  return productOrName?.name || '';
};

export const isCornProduct = (productOrName) => (
  getProductName(productOrName).toLowerCase().includes('corn')
);

export const isEggProduct = (productOrName) => (
  /\beggs?\b/i.test(getProductName(productOrName))
);

export const getCornBulkPricingParts = (quantityValue, originalUnitPrice) => {
  const quantity = normalizeQuantity(quantityValue);
  const unitPrice = toPriceNumber(originalUnitPrice);
  const dozens = Math.floor(quantity / CORN_DOZEN_QUANTITY);
  const afterDozens = quantity % CORN_DOZEN_QUANTITY;
  const halfDozens = Math.floor(afterDozens / CORN_HALF_DOZEN_QUANTITY);
  const remainder = afterDozens % CORN_HALF_DOZEN_QUANTITY;
  const totalPrice = (
    (dozens * CORN_DOZEN_PRICE)
    + (halfDozens * CORN_HALF_DOZEN_PRICE)
    + (remainder * unitPrice)
  );
  const totalSavings = Math.max(0, (quantity * unitPrice) - totalPrice);

  return {
    dozens,
    halfDozens,
    remainder,
    totalPrice,
    totalSavings
  };
};

export const getUnitLabel = (unit) => {
  const normalizedUnit = String(unit || '').trim().toLowerCase();

  if (!normalizedUnit || normalizedUnit === 'ea') {
    return 'each';
  }

  return normalizedUnit;
};

export const calculateSpecialPricing = (product, quantityValue) => {
  const quantity = normalizeQuantity(quantityValue);
  const originalUnitPrice = toPriceNumber(product?.originalPrice ?? product?.price);

  if (isCornProduct(product) && quantity >= CORN_HALF_DOZEN_QUANTITY) {
    const cornPricing = getCornBulkPricingParts(quantity, originalUnitPrice);

    if (cornPricing.totalSavings > 0) {
      return {
        type: 'corn-bulk',
        label: 'Corn bulk pricing',
        totalPrice: cornPricing.totalPrice,
        effectiveUnitPrice: cornPricing.totalPrice / quantity,
        hasSpecialPricing: true,
        dozens: cornPricing.dozens,
        halfDozens: cornPricing.halfDozens,
        remainder: cornPricing.remainder,
        halfDozenQuantity: CORN_HALF_DOZEN_QUANTITY,
        halfDozenPrice: CORN_HALF_DOZEN_PRICE,
        dozenQuantity: CORN_DOZEN_QUANTITY,
        dozenPrice: CORN_DOZEN_PRICE,
        originalUnitPrice,
        totalSavings: cornPricing.totalSavings
      };
    }

    return {
      type: null,
      label: null,
      totalPrice: originalUnitPrice * quantity,
      effectiveUnitPrice: originalUnitPrice,
      hasSpecialPricing: false,
      dozenPrice: CORN_DOZEN_PRICE,
      originalUnitPrice,
      totalSavings: 0
    };
  }

  if (
    isEggProduct(product)
    && quantity >= EGG_BULK_THRESHOLD
    && originalUnitPrice > EGG_BULK_UNIT_PRICE
  ) {
    const totalPrice = quantity * EGG_BULK_UNIT_PRICE;

    return {
      type: 'egg-bulk',
      label: 'Egg bulk pricing',
      totalPrice,
      effectiveUnitPrice: EGG_BULK_UNIT_PRICE,
      hasSpecialPricing: true,
      threshold: EGG_BULK_THRESHOLD,
      bulkUnitPrice: EGG_BULK_UNIT_PRICE,
      originalUnitPrice,
      totalSavings: (quantity * originalUnitPrice) - totalPrice
    };
  }

  return {
    type: null,
    label: null,
    totalPrice: originalUnitPrice * quantity,
    effectiveUnitPrice: originalUnitPrice,
    hasSpecialPricing: false,
    originalUnitPrice,
    totalSavings: 0
  };
};

export const buildSpecialPricingInfo = (product, quantityValue) => {
  const quantity = normalizeQuantity(quantityValue);
  const pricing = calculateSpecialPricing(product, quantity);

  if (!pricing.hasSpecialPricing) {
    return null;
  }

  if (pricing.type === 'corn-bulk') {
    return {
      type: pricing.type,
      label: pricing.label,
      dozens: pricing.dozens,
      halfDozens: pricing.halfDozens,
      remainder: pricing.remainder,
      halfDozenQuantity: pricing.halfDozenQuantity,
      halfDozenPrice: pricing.halfDozenPrice,
      dozenQuantity: pricing.dozenQuantity,
      dozenPrice: pricing.dozenPrice,
      originalUnitPrice: pricing.originalUnitPrice,
      totalSavings: pricing.totalSavings
    };
  }

  if (pricing.type === 'egg-bulk') {
    return {
      type: pricing.type,
      label: pricing.label,
      threshold: pricing.threshold,
      bulkUnitPrice: pricing.bulkUnitPrice,
      originalUnitPrice: pricing.originalUnitPrice,
      totalSavings: pricing.totalSavings
    };
  }

  return null;
};

export const applySpecialPricingToItem = (item, quantityValue = item?.quantity) => {
  const quantity = normalizeQuantity(quantityValue);
  const originalPrice = toPriceNumber(item?.originalPrice ?? item?.price);
  const normalizedItem = {
    ...item,
    price: toPriceNumber(item?.price, originalPrice),
    originalPrice
  };
  const pricing = calculateSpecialPricing(normalizedItem, quantity);

  return {
    ...normalizedItem,
    quantity,
    effectiveUnitPrice: pricing.effectiveUnitPrice,
    hasSpecialPricing: pricing.hasSpecialPricing,
    specialPricingInfo: buildSpecialPricingInfo(normalizedItem, quantity)
  };
};

export const getItemEffectiveUnitPrice = (item) => (
  toPriceNumber(item?.effectiveUnitPrice ?? item?.unitPrice ?? item?.price)
);

export const getItemOriginalUnitPrice = (item) => (
  toPriceNumber(
    item?.specialPricingInfo?.originalUnitPrice
      ?? item?.originalUnitPrice
      ?? item?.originalPrice
      ?? item?.unitPrice
      ?? item?.price
  )
);

export const getItemLineTotal = (item) => {
  const directTotal = parseFloat(item?.totalPrice);
  if (Number.isFinite(directTotal)) {
    return directTotal;
  }

  return getItemEffectiveUnitPrice(item) * normalizeQuantity(item?.quantity);
};

export const getItemOriginalLineTotal = (item) => {
  const directOriginalTotal = parseFloat(item?.originalTotalPrice);
  if (Number.isFinite(directOriginalTotal)) {
    return directOriginalTotal;
  }

  return getItemOriginalUnitPrice(item) * normalizeQuantity(item?.quantity);
};

export const getItemSavings = (item) => (
  Math.max(0, getItemOriginalLineTotal(item) - getItemLineTotal(item))
);

export const getCartSubtotal = (items = []) => (
  items.reduce((total, item) => total + getItemLineTotal(item), 0)
);

export const getCartOriginalSubtotal = (items = []) => (
  items.reduce((total, item) => total + getItemOriginalLineTotal(item), 0)
);

export const getCartSpecialSavings = (items = []) => (
  items.reduce((total, item) => total + getItemSavings(item), 0)
);

export const getCartEggBulkSavings = (items = []) => (
  items.reduce((total, item) => (
    item?.specialPricingInfo?.type === 'egg-bulk' ? total + getItemSavings(item) : total
  ), 0)
);

export const getCartCornBulkSavings = (items = []) => (
  items.reduce((total, item) => (
    item?.specialPricingInfo?.type === 'corn-bulk' ? total + getItemSavings(item) : total
  ), 0)
);

export const getBulkSavingsLabel = (items = []) => {
  const specialSavings = getCartSpecialSavings(items);
  const eggSavings = getCartEggBulkSavings(items);
  const cornSavings = getCartCornBulkSavings(items);

  if (specialSavings > 0 && Math.abs(specialSavings - eggSavings) < 0.005) {
    return 'Egg bulk pricing';
  }

  if (specialSavings > 0 && Math.abs(specialSavings - cornSavings) < 0.005) {
    return 'Corn bulk pricing';
  }

  return 'Bulk pricing savings';
};

export const getSpecialPricingDescription = (item) => {
  const info = item?.specialPricingInfo;
  if (!info) {
    return '';
  }

  if (info.type === 'egg-bulk') {
    const unitLabel = getUnitLabel(item?.unit || 'dozen');
    return `${info.threshold}+ ${unitLabel} price: $${info.bulkUnitPrice.toFixed(2)} / ${unitLabel}`;
  }

  if (info.type === 'corn-bulk' || info.type === 'corn-dozen') {
    const parts = [];
    if (info.dozens > 0) {
      parts.push(`${info.dozens} dozen @ $${info.dozenPrice.toFixed(2)}`);
    }
    if (info.halfDozens > 0) {
      const halfDozenText = info.halfDozens === 1 ? '1/2 dozen' : `${info.halfDozens} half dozens`;
      parts.push(`${halfDozenText} @ $${info.halfDozenPrice.toFixed(2)}`);
    }
    if (info.remainder > 0) {
      parts.push(`${info.remainder} each @ $${info.originalUnitPrice.toFixed(2)}`);
    }
    return parts.join(' + ');
  }

  return info.label || '';
};
